import { css } from "dx-styles";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { ApiError, api } from "../api/client";
import type { CollectionDetail } from "../api/types";
import {
  Modal,
  button,
  card,
  errorText,
  heading,
  muted,
  row,
  spread,
  stack,
  subheading,
} from "../components/primitives";
import type { CollectionDocument, Parameter, ParameterType, Template, TemplateRule } from "../lib/document";
import type { History } from "../lib/history";
import { canRedo, canUndo, emptyHistory, historyShortcut, pushHistory, redo, undo } from "../lib/history";
import {
  defaultValueFor,
  eligibleParents,
  mergedParameters,
  newId,
  newParameter,
  newTemplate,
  parameterValueText,
  parseParameterValue,
  templateById,
  withTemplate,
  withoutTemplate,
} from "../lib/document";
import { PreviewPanel } from "../components/PreviewPanel";
import { PublishDialog } from "../components/PublishDialog";
import { AddRuleMenu, RuleTree, RuleTreeHint } from "./RuleTree";
import { RuleForm } from "./RuleForm";
import { tokens } from "../theme/tokens";

const shell = css({
  display: "grid",
  gridTemplateColumns: "260px minmax(0, 1fr) 320px",
  gap: tokens.space.md,
  padding: tokens.space.md,
  alignItems: "start",
  "@media (max-width: 1100px)": { gridTemplateColumns: "1fr" },
});

const pane = css({
  backgroundColor: tokens.color.paper,
  border: `1px solid ${tokens.color.rule}`,
  borderRadius: tokens.radius.md,
  padding: tokens.space.md,
  display: "flex",
  flexDirection: "column",
  gap: tokens.space.sm,
});

const listButton = css({
  textAlign: "start",
  border: "1px solid transparent",
  borderRadius: tokens.radius.sm,
  background: "none",
  padding: `${tokens.space.xs} ${tokens.space.sm}`,
  cursor: "pointer",
  width: "100%",
});

const listButtonOn = css({ borderColor: tokens.color.olive, backgroundColor: tokens.color.kraft });

const field = css({
  minHeight: "34px",
  padding: `0 ${tokens.space.sm}`,
  borderRadius: tokens.radius.sm,
  border: `1px solid ${tokens.color.rule}`,
  backgroundColor: tokens.color.paper,
  width: "100%",
});

const area = css({
  padding: tokens.space.sm,
  borderRadius: tokens.radius.sm,
  border: `1px solid ${tokens.color.rule}`,
  backgroundColor: tokens.color.paper,
  width: "100%",
  minHeight: "60px",
  resize: "vertical",
});

const banner = css({
  border: `1px solid ${tokens.color.signal}`,
  borderRadius: tokens.radius.sm,
  padding: tokens.space.sm,
  backgroundColor: tokens.color.kraft,
});

const grid = css({ display: "grid", gridTemplateColumns: "1fr 1fr", gap: tokens.space.sm });

const saveStates = {
  idle: "Saved",
  saving: "Saving…",
  dirty: "Unsaved changes",
} as const;

type SaveState = keyof typeof saveStates;

interface Props {
  readonly onSignedOut: () => void;
}

/**
 * The collection editor: templates on the left, the selected template's form in
 * the centre, live preview on the right.
 *
 * The document is the unit of editing — one JSON object, saved whole with
 * optimistic concurrency. That is what makes undo (task 14.17) a stack of
 * documents rather than a log of operations.
 */
export function Editor({ onSignedOut }: Props) {
  const { collectionId = "" } = useParams();
  const [detail, setDetail] = useState<CollectionDetail | null>(null);
  const [document, setDocument] = useState<CollectionDocument | null>(null);
  const [version, setVersion] = useState(0);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [selectedRuleId, setSelectedRuleId] = useState<string | null>(null);
  /// Rules the engine complained about, so the tree can mark them where the
  /// author is looking instead of printing a list somewhere else.
  const [issueRuleIds, setIssueRuleIds] = useState<readonly string[]>([]);
  const [publishOpen, setPublishOpen] = useState(false);
  const [history, setHistory] = useState<History>(emptyHistory);
  const [revertOpen, setRevertOpen] = useState(false);
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const pendingSave = useRef<number | null>(null);
  /// One save at a time. Two overlapping PATCHes both carry the version the
  /// editor knew when they were scheduled, so the second one 409s — and the
  /// editor accuses another writer of a change it made itself. A fast typist hits
  /// this; a script hits it every time.
  const saveQueue = useRef<{ inFlight: boolean; pending: CollectionDocument | null }>({
    inFlight: false,
    pending: null,
  });
  const latestVersion = useRef(0);

  const load = useCallback(async () => {
    try {
      const [loadedDetail, draft] = await Promise.all([api.collection(collectionId), api.draft(collectionId)]);
      setDetail(loadedDetail);
      setDocument(draft.document as CollectionDocument);
      setVersion(draft.version);
      latestVersion.current = draft.version;
      saveQueue.current = { inFlight: false, pending: null };
      setConflict(false);
      setSaveState("idle");
      setSelectedTemplateId((current) => current ?? (draft.document as CollectionDocument).templates[0]?.id ?? null);
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) {
        onSignedOut();
        return;
      }
      setError(caught instanceof Error ? caught.message : "Could not open this collection.");
    }
  }, [collectionId, onSignedOut]);

  useEffect(() => {
    void load();
  }, [load]);

  const send = useCallback(
    async (candidate: CollectionDocument): Promise<void> => {
      if (saveQueue.current.inFlight) {
        // Keep only the newest: intermediate states are not worth a round trip.
        saveQueue.current.pending = candidate;
        return;
      }
      saveQueue.current.inFlight = true;
      setSaveState("saving");
      try {
        const newVersion = await api.saveDraft(collectionId, latestVersion.current, candidate);
        if (newVersion === null) {
          // A genuine other writer: nothing is written until the author decides.
          setConflict(true);
          setSaveState("dirty");
          return;
        }
        latestVersion.current = newVersion;
        setVersion(newVersion);
        setError(null);
        setSaveState(saveQueue.current.pending ? "dirty" : "idle");
      } catch (caught) {
        setSaveState("dirty");
        setError(caught instanceof Error ? caught.message : "That change could not be saved.");
      } finally {
        saveQueue.current.inFlight = false;
        const queued = saveQueue.current.pending;
        saveQueue.current.pending = null;
        if (queued) await send(queued);
      }
    },
    [collectionId],
  );

  /// Debounced autosave — there is no Save button, because Publish is already
  /// the explicit act.
  const scheduleSave = useCallback(
    (next: CollectionDocument, options: { readonly coalesce?: boolean; readonly recordHistory?: boolean } = {}) => {
      if (options.recordHistory !== false) {
        setDocument((current) => {
          if (current) setHistory((past) => pushHistory(past, current, { coalesce: options.coalesce ?? false }));
          return current;
        });
      }
      setDocument(next);
      setSaveState("dirty");
      if (pendingSave.current !== null) window.clearTimeout(pendingSave.current);
      pendingSave.current = window.setTimeout(() => {
        void send(next);
      }, 800);
    },
    [send],
  );

  const selected = useMemo(
    () => (document && selectedTemplateId ? templateById(document, selectedTemplateId) : undefined),
    [document, selectedTemplateId],
  );

  const updateTemplate = useCallback(
    (template: Template, options: { readonly coalesce?: boolean } = {}) => {
      if (!document) return;
      scheduleSave(withTemplate(document, template), options);
    },
    [document, scheduleSave],
  );

  /// Rules live inside templates, and a conditional's children inside the rule,
  /// so every edit is expressed as "replace this rule wherever it is".
  const updateRule = useCallback(
    (rule: TemplateRule) => {
      if (!document || !selected) return;
      // Typing in a rule field is one undo step per burst, not per keystroke.
      updateTemplate({ ...selected, rules: replaceRule(selected.rules, rule) }, { coalesce: true });
    },
    [document, selected, updateTemplate],
  );

  const deleteRule = useCallback(
    (ruleId: string) => {
      if (!selected) return;
      setSelectedRuleId(null);
      updateTemplate({ ...selected, rules: removeRule(selected.rules, ruleId) });
    },
    [selected, updateTemplate],
  );

  const applyHistory = useCallback(
    (direction: "undo" | "redo") => {
      if (!document) return;
      const step = direction === "undo" ? undo(history, document) : redo(history, document);
      if (!step) return;
      setHistory(step.history);
      // The restored document is saved like any other edit, so the server and the
      // preview follow along — but it must not push itself onto the stack.
      scheduleSave(step.document, { recordHistory: false });
    },
    [document, history, scheduleSave],
  );

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const shortcut = historyShortcut(event);
      if (!shortcut) return;
      event.preventDefault();
      applyHistory(shortcut);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [applyHistory]);

  const selectedRule = useMemo(
    () => (selected && selectedRuleId ? findRule(selected.rules, selectedRuleId) : undefined),
    [selected, selectedRuleId],
  );

  if (error && !document) {
    return (
      <main className={shell}>
        <p className={errorText}>{error}</p>
      </main>
    );
  }

  if (!document || !detail) {
    return (
      <main className={shell}>
        <p className={muted}>Opening…</p>
      </main>
    );
  }

  return (
    <>
      <div className={spread} style={{ padding: `${0} ${16}px`, maxWidth: "1400px", margin: "0 auto" }}>
        <div className={stack}>
          <h1 className={heading}>{detail.name}</h1>
          <span className={muted}>
            {saveStates[saveState]}
            {detail.isPublished ? ` · published rev ${detail.publishedRevision}` : " · never published"}
          </span>
        </div>
        <div className={row}>
          <button
            type="button"
            className={button({ size: "sm" })}
            disabled={!canUndo(history)}
            onClick={() => applyHistory("undo")}
            title="Undo (⌘Z)"
          >
            Undo
          </button>
          <button
            type="button"
            className={button({ size: "sm" })}
            disabled={!canRedo(history)}
            onClick={() => applyHistory("redo")}
            title="Redo (⇧⌘Z)"
          >
            Redo
          </button>
          <button
            type="button"
            className={button({ size: "sm" })}
            disabled={!detail.isPublished}
            onClick={() => setRevertOpen(true)}
          >
            Revert to published…
          </button>
          <button
            type="button"
            className={button({ tone: "primary" })}
            // Autosave has to have landed: the dialog diffs the *saved* document.
            disabled={saveState !== "idle"}
            onClick={() => setPublishOpen(true)}
          >
            Publish…
          </button>
        </div>
      </div>

      <Modal
        open={revertOpen}
        onOpenChange={setRevertOpen}
        title="Revert this draft?"
        description={`Everything since revision ${detail.publishedRevision ?? "?"} is discarded, and the draft goes back to exactly what subscribers are getting.`}
      >
        <div className={row}>
          <button
            type="button"
            className={button({ tone: "danger" })}
            onClick={() => {
              void (async () => {
                try {
                  await api.revertDraft(collectionId);
                  setHistory(emptyHistory);
                  setRevertOpen(false);
                  await load();
                } catch (caught) {
                  setError(caught instanceof Error ? caught.message : "Could not revert the draft.");
                }
              })();
            }}
          >
            Discard my changes
          </button>
          <button type="button" className={button()} onClick={() => setRevertOpen(false)}>
            Keep editing
          </button>
        </div>
      </Modal>

      <PublishDialog
        collectionId={collectionId}
        collectionName={detail.name}
        open={publishOpen}
        onOpenChange={setPublishOpen}
        onPublished={() => void load()}
      />

      {conflict ? (
        <div className={banner} role="alert">
          <div className={stack}>
            <strong>This collection changed somewhere else.</strong>
            <span className={muted}>
              Nothing has been overwritten. Reload to take the server&rsquo;s copy — your edits since the last
              save are lost — or keep editing and save again to overwrite theirs.
            </span>
            <div className={row}>
              <button type="button" className={button({ tone: "primary", size: "sm" })} onClick={() => void load()}>
                Reload server copy
              </button>
              <button
                type="button"
                className={button({ size: "sm" })}
                onClick={() => {
                  void (async () => {
                    const draft = await api.draft(collectionId);
                    setVersion(draft.version);
                    setConflict(false);
                    scheduleSave(document);
                  })();
                }}
              >
                Overwrite with mine
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div className={shell}>
        <section className={pane} aria-label="Templates">
          <h2 className={subheading}>Templates</h2>
          {document.templates.length === 0 ? <span className={muted}>None yet.</span> : null}
          {document.templates.map((template) => (
            <button
              key={template.id}
              type="button"
              className={template.id === selectedTemplateId ? `${listButton} ${listButtonOn}` : listButton}
              onClick={() => setSelectedTemplateId(template.id)}
            >
              <div className={stack}>
                <strong>{template.title || "Untitled template"}</strong>
                <span className={muted}>
                  {template.rules.length} rule{template.rules.length === 1 ? "" : "s"}
                  {template.parentIDs.length > 0 ? ` · inherits ${template.parentIDs.length}` : ""}
                </span>
              </div>
            </button>
          ))}
          <button
            type="button"
            className={button({ size: "sm" })}
            onClick={() => {
              const template = newTemplate("New template");
              setSelectedTemplateId(template.id);
              scheduleSave(withTemplate(document, template));
            }}
          >
            Add template
          </button>

          {selected ? (
            <div className={stack}>
              <h2 className={subheading}>Rules</h2>
              <RuleTreeHint />
              <RuleTree
                rules={selected.rules}
                items={document.items}
                parameters={mergedParameters(document, selected.id)}
                selectedRuleId={selectedRuleId}
                issueRuleIds={issueRuleIds}
                onSelect={setSelectedRuleId}
                onReorder={(rules) => updateTemplate({ ...selected, rules })}
                onChangeRule={updateRule}
              />
              <AddRuleMenu
                onAdd={(type) => {
                  const rule: TemplateRule = { id: newId(), type };
                  setSelectedRuleId(rule.id);
                  updateTemplate({ ...selected, rules: [...selected.rules, rule] });
                }}
              />
            </div>
          ) : null}
        </section>

        <section className={pane} aria-label={selectedRule ? "Rule" : "Template"}>
          {selectedRule && selected ? (
            <>
              <button type="button" className={button({ size: "sm" })} onClick={() => setSelectedRuleId(null)}>
                ← Back to the template
              </button>
              <RuleForm
                collectionId={collectionId}
                rule={selectedRule}
                items={document.items}
                parameters={mergedParameters(document, selected.id)}
                depth={depthOfRule(selected.rules, selectedRule.id)}
                onChange={updateRule}
                onDelete={() => deleteRule(selectedRule.id)}
              />
            </>
          ) : selected ? (
            <TemplateForm
              document={document}
              template={selected}
              onChange={updateTemplate}
              onDelete={() => {
                setSelectedTemplateId(null);
                setSelectedRuleId(null);
                scheduleSave(withoutTemplate(document, selected.id));
              }}
            />
          ) : (
            <span className={muted}>Pick a template on the left, or add one.</span>
          )}
          {error ? <p className={errorText}>{error}</p> : null}
        </section>

        <section className={pane}>
          <PreviewPanel
            collectionId={collectionId}
            templateId={selected?.id ?? null}
            parameters={selected ? mergedParameters(document, selected.id) : []}
            documentVersion={version}
            // The panel previews the *saved* document. Parameter changes still
            // take effect while an edit is in flight; the panel says so rather
            // than freezing.
            isDirty={saveState !== "idle"}
            onIssueRules={setIssueRuleIds}
          />
        </section>
      </div>
    </>
  );
}

function TemplateForm({
  document,
  template,
  onChange,
  onDelete,
}: {
  readonly document: CollectionDocument;
  readonly template: Template;
  readonly onChange: (template: Template) => void;
  readonly onDelete: () => void;
}) {
  const parents = eligibleParents(document, template.id);
  const inherited = mergedParameters(document, template.id).filter(
    (parameter) => !template.parameters.some((own) => own.id === parameter.id),
  );

  return (
    <div className={stack}>
      <div className={spread}>
        <h2 className={subheading}>Template</h2>
        <button type="button" className={button({ tone: "danger", size: "sm" })} onClick={onDelete}>
          Delete template
        </button>
      </div>

      <label className={stack}>
        <span className={muted}>Title</span>
        <input
          className={field}
          value={template.title}
          onChange={(event) => onChange({ ...template, title: event.target.value })}
        />
      </label>

      <label className={stack}>
        <span className={muted}>Description</span>
        <textarea
          className={area}
          value={template.description ?? ""}
          onChange={(event) => onChange({ ...template, description: event.target.value || null })}
        />
      </label>

      <fieldset className={stack} style={{ border: "none", padding: 0, margin: 0 }}>
        <legend className={muted}>Inherits from</legend>
        {parents.length === 0 ? (
          <span className={muted}>No other template in this collection can be a parent yet.</span>
        ) : (
          parents.map((candidate) => (
            <label key={candidate.id} className={row}>
              <input
                type="checkbox"
                checked={template.parentIDs.includes(candidate.id)}
                onChange={(event) =>
                  onChange({
                    ...template,
                    parentIDs: event.target.checked
                      ? [...template.parentIDs, candidate.id]
                      : template.parentIDs.filter((id) => id !== candidate.id),
                  })
                }
              />
              <span>{candidate.title || "Untitled template"}</span>
            </label>
          ))
        )}
        <span className={muted}>
          Inheritance stays inside this collection — a parent elsewhere would resolve to nothing on a
          subscriber&rsquo;s phone. Descendants are hidden, so a cycle cannot be built.
        </span>
      </fieldset>

      <div className={stack}>
        <div className={spread}>
          <h3 className={subheading}>Parameters</h3>
          <button
            type="button"
            className={button({ size: "sm" })}
            onClick={() => onChange({ ...template, parameters: [...template.parameters, newParameter()] })}
          >
            Add parameter
          </button>
        </div>
        {inherited.length > 0 ? (
          <span className={muted}>
            Inherited: {inherited.map((parameter) => parameter.key).join(", ")} — edit them on the template
            that declares them.
          </span>
        ) : null}
        {template.parameters.length === 0 ? <span className={muted}>None on this template.</span> : null}
        {template.parameters.map((parameter) => (
          <ParameterRow
            key={parameter.id}
            parameter={parameter}
            onChange={(next) =>
              onChange({
                ...template,
                parameters: template.parameters.map((candidate) =>
                  candidate.id === parameter.id ? next : candidate,
                ),
              })
            }
            onRemove={() =>
              onChange({
                ...template,
                parameters: template.parameters.filter((candidate) => candidate.id !== parameter.id),
              })
            }
          />
        ))}
        <span className={muted}>
          Renaming or removing a parameter that rules still use is refused when the draft saves — the same
          refusal the app makes.
        </span>
      </div>
    </div>
  );
}

function ParameterRow({
  parameter,
  onChange,
  onRemove,
}: {
  readonly parameter: Parameter;
  readonly onChange: (parameter: Parameter) => void;
  readonly onRemove: () => void;
}) {
  const numeric = parameter.type === "integer" || parameter.type === "double";
  return (
    <div className={card}>
      <div className={stack}>
        <div className={grid}>
          <label className={stack}>
            <span className={muted}>Key</span>
            <input
              className={field}
              value={parameter.key}
              onChange={(event) => onChange({ ...parameter, key: event.target.value })}
              autoComplete="off"
              spellCheck={false}
            />
          </label>
          <label className={stack}>
            <span className={muted}>Type</span>
            <select
              className={field}
              value={parameter.type}
              onChange={(event) => {
                const type = event.target.value as ParameterType;
                // Switching type resets the default and drops constraints that
                // cannot apply, so the document is never internally inconsistent.
                onChange({
                  ...parameter,
                  type,
                  defaultValue: defaultValueFor(type),
                  constraints: null,
                });
              }}
            >
              <option value="integer">integer</option>
              <option value="double">double</option>
              <option value="boolean">boolean</option>
              <option value="string">string (enum)</option>
            </select>
          </label>
        </div>

        <div className={grid}>
          <label className={stack}>
            <span className={muted}>Default</span>
            {parameter.type === "boolean" ? (
              <select
                className={field}
                value={String(parameter.defaultValue?.value ?? false)}
                onChange={(event) =>
                  onChange({ ...parameter, defaultValue: { type: "bool", value: event.target.value === "true" } })
                }
              >
                <option value="false">false</option>
                <option value="true">true</option>
              </select>
            ) : (
              <input
                className={field}
                value={parameterValueText(parameter.defaultValue)}
                onChange={(event) =>
                  onChange({
                    ...parameter,
                    defaultValue: parseParameterValue(parameter.type, event.target.value) ?? parameter.defaultValue,
                  })
                }
              />
            )}
          </label>
          <label className={stack}>
            <span className={muted}>UI hint</span>
            <input
              className={field}
              value={parameter.uiHint ?? ""}
              onChange={(event) => onChange({ ...parameter, uiHint: event.target.value || null })}
              placeholder="stepper, switch…"
            />
          </label>
        </div>

        {numeric ? (
          <div className={grid}>
            <label className={stack}>
              <span className={muted}>Min</span>
              <input
                className={field}
                value={parameter.constraints?.min ?? ""}
                onChange={(event) =>
                  onChange({
                    ...parameter,
                    constraints: {
                      ...parameter.constraints,
                      min: event.target.value === "" ? null : Number(event.target.value),
                    },
                  })
                }
              />
            </label>
            <label className={stack}>
              <span className={muted}>Max</span>
              <input
                className={field}
                value={parameter.constraints?.max ?? ""}
                onChange={(event) =>
                  onChange({
                    ...parameter,
                    constraints: {
                      ...parameter.constraints,
                      max: event.target.value === "" ? null : Number(event.target.value),
                    },
                  })
                }
              />
            </label>
          </div>
        ) : null}

        {parameter.type === "string" ? (
          <label className={stack}>
            <span className={muted}>Allowed values (comma separated)</span>
            <input
              className={field}
              value={(parameter.constraints?.allowed ?? []).join(", ")}
              onChange={(event) =>
                onChange({
                  ...parameter,
                  constraints: {
                    ...parameter.constraints,
                    allowed: event.target.value
                      .split(",")
                      .map((value) => value.trim())
                      .filter((value) => value.length > 0),
                  },
                })
              }
            />
          </label>
        ) : null}

        <div className={row}>
          <button type="button" className={button({ tone: "danger", size: "sm" })} onClick={onRemove}>
            Remove parameter
          </button>
        </div>
      </div>
    </div>
  );
}

/** Replaces a rule anywhere in the tree, including inside a conditional. */
function replaceRule(rules: readonly TemplateRule[], replacement: TemplateRule): readonly TemplateRule[] {
  return rules.map((rule) => {
    if (rule.id === replacement.id) return replacement;
    if (rule.conditionalRules) {
      return { ...rule, conditionalRules: replaceRule(rule.conditionalRules, replacement) };
    }
    return rule;
  });
}

function removeRule(rules: readonly TemplateRule[], ruleId: string): readonly TemplateRule[] {
  return rules
    .filter((rule) => rule.id !== ruleId)
    .map((rule) =>
      rule.conditionalRules ? { ...rule, conditionalRules: removeRule(rule.conditionalRules, ruleId) } : rule,
    );
}

function findRule(rules: readonly TemplateRule[], ruleId: string): TemplateRule | undefined {
  for (const rule of rules) {
    if (rule.id === ruleId) return rule;
    const nested = rule.conditionalRules ? findRule(rule.conditionalRules, ruleId) : undefined;
    if (nested) return nested;
  }
  return undefined;
}

/** 1 for a top-level rule, 2 for one inside a conditional, and so on. */
function depthOfRule(rules: readonly TemplateRule[], ruleId: string, depth = 1): number {
  for (const rule of rules) {
    if (rule.id === ruleId) return depth;
    if (rule.conditionalRules) {
      const found = depthOfRule(rule.conditionalRules, ruleId, depth + 1);
      if (found > 0) return found;
    }
  }
  return 0;
}
